import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ShieldCheck, User, Clock, Fingerprint, Check, FileText, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  getDocumentSubmission,
  type DocumentBlock,
  type DocumentSubmissionFull,
} from '../../lib/documents';

interface SubmissionViewerModalProps {
  submissionId: string;
  onClose: () => void;
}

function fmt(ts: string | null | undefined): string {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function SubmissionViewerModal({ submissionId, onClose }: SubmissionViewerModalProps) {
  const [sub, setSub] = useState<DocumentSubmissionFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-document-pdf', { body: { submissionId } });
      if (error || !data?.base64) {
        const msg = (data as any)?.error || (error as any)?.message;
        alert(msg ? `Failed to generate PDF: ${msg}` : 'Failed to generate PDF. Please try again.');
        return;
      }
      const binaryStr = atob(data.base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const filename: string = data.filename || 'document.pdf';

      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: filename }).catch(() => {});
        }
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Error downloading document PDF:', e);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try { const data = await getDocumentSubmission(submissionId); if (active) setSub(data); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [submissionId]);

  const respByBlock: Record<string, any> = {};
  for (const r of sub?.responses || []) respByBlock[r.block_id] = r.value;
  const sigByBlock: Record<string, string> = {};
  const sigsByBlock: Record<string, typeof sub.signatures> = {};
  for (const s of sub?.signatures || []) {
    if (s.signature_data) sigByBlock[s.block_id] = s.signature_data;
    if (!sigsByBlock[s.block_id]) sigsByBlock[s.block_id] = [];
    sigsByBlock[s.block_id].push(s);
  }

  const content = (
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-stretch justify-center sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="bg-gray-100 shadow-xl w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[92vh] flex flex-col rounded-none sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white sm:rounded-t-2xl shrink-0"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">Completed document</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 px-1.5 py-0.5 rounded"><ShieldCheck className="h-3 w-3" /> Locked</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={downloading || loading || !sub}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">{downloading ? 'Generating…' : 'Download PDF'}</span>
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}>
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !sub ? (
            <div className="text-center py-16 text-gray-400 text-sm">Submission not found.</div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {/* Rendered document */}
              <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-5">
                <h1 className="text-2xl font-bold text-gray-900">{sub.title}</h1>
                <div className="h-px bg-gray-100" />
                {(sub.template_snapshot || []).map((b) => (
                  <FilledBlock key={b.id} block={b} value={respByBlock[b.id]} signature={sigByBlock[b.id]} signers={sigsByBlock[b.id]} />
                ))}
              </div>

              {/* Legal metadata */}
              <div className="space-y-3">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Completion record</div>
                  <Meta icon={User} label="Completed by" value={sub.submitted_by_name || '—'} sub={sub.submitted_by_email || undefined} />
                  <Meta icon={Clock} label="Submitted at" value={fmt(sub.submitted_at)} />
                  <Meta icon={Check} label="Consent" value={sub.consent_at ? `Agreed ${fmt(sub.consent_at)}` : 'Not recorded'} />
                  <Meta icon={User} label="IP address" value={sub.ip_address || '—'} />
                  <Meta icon={Fingerprint} label="Integrity hash" value={sub.content_hash ? `${sub.content_hash.slice(0, 16)}…` : '—'} mono title={sub.content_hash || undefined} />
                </div>

                {sub.verification_status === 'pending' && (
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-amber-700 mb-1 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Awaiting sign-off</div>
                    <p className="text-[12px] text-amber-700">This document is complete but awaiting verification sign-off before it is finalized.</p>
                  </div>
                )}

                {sub.verification_status === 'verified' && (() => {
                  const vsig = (sub.signatures || []).find((s) => s.is_verification);
                  return (
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Verification sign-off</div>
                      {vsig?.signature_data && <img src={vsig.signature_data} alt="verifier signature" className="h-14 border border-gray-200 rounded bg-white" />}
                      <div className="text-[12px] text-gray-700 mt-1">{sub.verified_by_name || vsig?.signer_name || '—'}</div>
                      <div className="text-[11px] text-gray-400">{fmt(sub.verified_at)}</div>
                    </div>
                  );
                })()}

                {sub.signatures.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Signatures</div>
                    {sub.signatures.map((s) => (
                      <div key={s.id} className="mb-3 last:mb-0">
                        {s.signature_data && <img src={s.signature_data} alt="signature" className="h-14 border border-gray-200 rounded bg-white" />}
                        <div className="text-[12px] text-gray-700 mt-1">{s.signer_name}</div>
                        <div className="text-[11px] text-gray-400">{fmt(s.signed_at)}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Audit trail</div>
                  <div className="space-y-2">
                    {sub.audit.map((a) => (
                      <div key={a.id} className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="text-[12px] font-medium text-gray-800 capitalize">{a.event_type}</div>
                          <div className="text-[11px] text-gray-400">{fmt(a.created_at)}{a.actor_name ? ` · ${a.actor_name}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function Meta({ icon: Icon, label, value, mono, title, sub }: { icon: any; label: string; value: string; mono?: boolean; title?: string; sub?: string }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0">
      <Icon className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] text-gray-400">{label}</div>
        <div className={`text-[13px] text-gray-800 break-words ${mono ? 'font-mono text-[11px]' : ''}`} title={title}>{value}</div>
        {sub && sub !== value && <div className="text-[12px] text-gray-500 break-words">{sub}</div>}
      </div>
    </div>
  );
}

function FilledBlock({ block, value, signature, signers }: { block: DocumentBlock; value: any; signature?: string; signers?: { id: string; signer_name: string | null; signature_data: string | null; signed_at: string }[] }) {
  const c = block.config || {};
  const answerCls = 'text-[14px] text-gray-900';
  switch (block.type) {
    case 'heading': {
      const size = c.size === 'h1' ? 'text-2xl' : c.size === 'h3' ? 'text-base' : 'text-xl';
      return <div className={`${size} font-bold text-gray-900`}>{c.text}</div>;
    }
    case 'rich_text':
      return <p className="text-[14px] text-gray-700 whitespace-pre-wrap leading-relaxed">{c.text}</p>;
    case 'divider':
      return <hr className="border-gray-200" />;
    case 'image':
      return c.url ? <img src={c.url} alt={c.caption || ''} className="max-h-56 rounded-lg" /> : null;
    case 'checklist':
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}</div>}
          <ul className="space-y-1.5">
            {(c.items || []).map((it: any) => (
              <li key={it.id} className="flex items-center gap-2.5">
                <span className={`h-4 w-4 rounded border flex items-center justify-center ${value?.[it.id] ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                  {value?.[it.id] && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="text-[14px] text-gray-700">{it.text}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'acknowledge':
      return (
        <div className="flex items-start gap-2.5">
          <span className={`h-4 w-4 rounded border mt-0.5 flex items-center justify-center ${value ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>{value && <Check className="h-3 w-3 text-white" />}</span>
          <span className="text-[14px] text-gray-700">{c.label}</span>
        </div>
      );
    case 'multiple_choice': {
      const selected: string[] = Array.isArray(value) ? value : value ? [value] : [];
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}</div>
          <div className="space-y-1.5">
            {(c.options || []).map((opt: any) => (
              <div key={opt.id} className="flex items-center gap-2.5">
                <span className={`h-4 w-4 border flex items-center justify-center ${c.multiple ? 'rounded' : 'rounded-full'} ${selected.includes(opt.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>{selected.includes(opt.id) && <Check className="h-3 w-3 text-white" />}</span>
                <span className="text-[14px] text-gray-700">{opt.text}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case 'short_text':
    case 'long_text':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1">{c.label}</div>
          <div className={`${answerCls} whitespace-pre-wrap`}>{value || <span className="text-gray-300">—</span>}</div>
        </div>
      );
    case 'number':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1">{c.label}</div>
          <div className={answerCls}>{value != null && value !== '' ? `${value}${c.unit ? ` ${c.unit}` : ''}` : <span className="text-gray-300">—</span>}</div>
        </div>
      );
    case 'date': {
      const display = value?.auto ? `${fmt(value.stampedAt)} (auto-stamped)` : value;
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1">{c.label}</div>
          <div className={answerCls}>{display || <span className="text-gray-300">—</span>}</div>
        </div>
      );
    }
    case 'time':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1">{c.label}</div>
          <div className={answerCls}>{value || <span className="text-gray-300">—</span>}</div>
        </div>
      );
    case 'signature':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}</div>
          {signature ? <img src={signature} alt="signature" className="h-16 border border-gray-200 rounded bg-white" /> : <span className="text-gray-300 text-sm">Not signed</span>}
        </div>
      );
    case 'file_upload':
    case 'photo':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1">{c.label}</div>
          <div className="flex items-center gap-1.5 text-[13px] text-gray-700"><FileText className="h-4 w-4 text-gray-400" /> {value || <span className="text-gray-300">No file</span>}</div>
        </div>
      );
    case 'table': {
      const columns: { id: string; label: string }[] = Array.isArray(c.columns) ? c.columns : [];
      const rows: { id: string; cells: Record<string, string> }[] = Array.isArray(value?.rows) ? value.rows : [];
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}</div>}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {columns.map((col) => (
                    <th key={col.id} className="border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={columns.length} className="border border-gray-200 px-2 py-2 text-gray-300 text-center">No entries</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((col) => (
                      <td key={col.id} className="border border-gray-200 px-2 py-2 text-gray-900 align-top whitespace-pre-wrap">{row.cells?.[col.id] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    case 'additional_signatures': {
      const list = signers || [];
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}</div>}
          {list.length === 0 ? (
            <span className="text-gray-300 text-sm">No additional signatures</span>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {list.map((s) => (
                <div key={s.id} className="border border-gray-200 rounded-lg p-2.5">
                  {s.signature_data ? <img src={s.signature_data} alt="signature" className="h-14 border border-gray-200 rounded bg-white" /> : <span className="text-gray-300 text-xs">Not signed</span>}
                  <div className="text-[13px] font-medium text-gray-800 mt-1">{s.signer_name || '—'}</div>
                  <div className="text-[11px] text-gray-400">{fmt(s.signed_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}
